import { useMemo } from 'react';
import { Building2, Download, Focus, MapPin, Settings2 } from 'lucide-react';
import type { EChartsCoreOption } from 'echarts/core';
import Chart from '../components/Chart';
import { buildTrend, penetrationByGeography } from '../lib/analytics';
import { exportPenetration } from '../lib/exporter';
import { formatCompactCurrency, formatNumber, formatPercent } from '../lib/format';
import type { SalesRecord, SummaryMetrics } from '../types';

interface PenetrationViewProps {
  records: SalesRecord[];
  summary: SummaryMetrics;
  targetProduct: string;
  dateLabel: string;
  onOpenTargets: () => void;
}

const axisText = { color: '#7d899d', fontFamily: 'Inter, Microsoft YaHei, sans-serif' };

export default function PenetrationView({ records, summary, targetProduct, dateLabel, onOpenTargets }: PenetrationViewProps) {
  const trend = useMemo(() => buildTrend(records, targetProduct), [records, targetProduct]);
  const geography = useMemo(() => penetrationByGeography(records, targetProduct), [records, targetProduct]);
  const targetTerminals = Math.round((summary.penetration / 100) * summary.terminals);
  const gap = Math.max(0, summary.terminals - targetTerminals);

  const trendOption = useMemo<EChartsCoreOption>(() => ({
    color: ['#2563eb', '#18a394'],
    tooltip: { trigger: 'axis', backgroundColor: '#172033', borderWidth: 0, textStyle: { color: '#fff' } },
    legend: { top: 0, right: 0, icon: 'circle', itemWidth: 8, itemHeight: 8, textStyle: axisText },
    grid: { left: 18, right: 18, top: 42, bottom: 8, containLabel: true },
    xAxis: { type: 'category', data: trend.map((item) => item.label), boundaryGap: false, axisTick: { show: false }, axisLine: { lineStyle: { color: '#e8edf5' } }, axisLabel: axisText },
    yAxis: [
      { type: 'value', max: 100, axisLabel: { ...axisText, formatter: '{value}%' }, splitLine: { lineStyle: { color: '#eef2f7', type: 'dashed' } } },
      { type: 'value', axisLabel: { ...axisText, formatter: (value: number) => `${Math.round(value / 10000)}万` }, splitLine: { show: false } },
    ],
    series: [
      {
        name: '渗透率', type: 'line', smooth: 0.35, symbolSize: 7, showSymbol: trend.length < 18,
        lineStyle: { width: 3 }, areaStyle: { opacity: 0.12 }, data: trend.map((item) => Number(item.penetration.toFixed(2))),
      },
      {
        name: '总销售额', type: 'bar', yAxisIndex: 1, barMaxWidth: 18, itemStyle: { borderRadius: [5, 5, 0, 0], opacity: 0.42 },
        data: trend.map((item) => Number(item.sales.toFixed(2))),
      },
    ],
  }), [trend]);

  const geoTop = geography.slice().sort((a, b) => b.penetration - a.penetration).slice(0, 12).reverse();
  const geoOption = useMemo<EChartsCoreOption>(() => ({
    color: ['#18a394'],
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, backgroundColor: '#172033', borderWidth: 0, textStyle: { color: '#fff' }, valueFormatter: (value: unknown) => formatPercent(Number(value)) },
    grid: { left: 8, right: 22, top: 8, bottom: 8, containLabel: true },
    xAxis: { type: 'value', max: 100, axisLabel: { ...axisText, formatter: '{value}%' }, splitLine: { lineStyle: { color: '#eef2f7', type: 'dashed' } } },
    yAxis: { type: 'category', data: geoTop.map((item) => item.city), axisTick: { show: false }, axisLine: { show: false }, axisLabel: { ...axisText, width: 86, overflow: 'truncate' } },
    series: [{ type: 'bar', barWidth: 10, itemStyle: { borderRadius: 8 }, data: geoTop.map((item) => Number(item.penetration.toFixed(2))) }],
  }), [geoTop]);

  return (
    <div className="view-stack">
      <section className="focus-banner">
        <div className="focus-copy">
          <span className="eyebrow">重点单品</span>
          <h2>{targetProduct}</h2>
          <p>渗透率 = 购买该单品的活跃终端数 ÷ 当前范围内全部活跃终端数。</p>
        </div>
        <button className="secondary-button" type="button" onClick={onOpenTargets}><Settings2 size={16} /> 更换单品</button>
      </section>

      <section className="mini-metric-grid">
        <article className="mini-metric"><span className="mini-icon blue"><Focus size={18} /></span><div><span>当前渗透率</span><strong>{formatPercent(summary.penetration)}</strong><small>{targetTerminals} / {summary.terminals} 家</small></div></article>
        <article className="mini-metric"><span className="mini-icon teal"><Building2 size={18} /></span><div><span>已渗透终端</span><strong>{formatNumber(targetTerminals)} 家</strong><small>覆盖重点单品</small></div></article>
        <article className="mini-metric"><span className="mini-icon amber"><MapPin size={18} /></span><div><span>待开发终端</span><strong>{formatNumber(gap)} 家</strong><small>优先跟进高价值终端</small></div></article>
        <article className="mini-metric"><span className="mini-icon violet"><Focus size={18} /></span><div><span>单品销售额</span><strong>{formatCompactCurrency(summary.targetProductSales)}</strong><small>{formatNumber(summary.targetProductQuantity)} 盒</small></div></article>
      </section>

      <section className="dashboard-grid dashboard-grid-even">
        <article className="panel">
          <div className="panel-head"><div><span className="eyebrow">覆盖变化</span><h3>单品渗透趋势</h3></div></div>
          <Chart option={trendOption} className="chart-tall" ariaLabel="重点单品终端渗透率和销售额趋势图" />
        </article>
        <article className="panel">
          <div className="panel-head"><div><span className="eyebrow">区域对比</span><h3>城市渗透率 Top 12</h3></div></div>
          <Chart option={geoOption} className="chart-tall" ariaLabel="各城市重点单品渗透率横向条形图" />
        </article>
      </section>

      <section className="panel table-panel">
        <div className="panel-head">
          <div><span className="eyebrow">区域明细</span><h3>终端覆盖机会清单</h3></div>
          <button className="secondary-button" type="button" onClick={() => void exportPenetration(geography, targetProduct, dateLabel)}><Download size={16} /> 下载渗透明细</button>
        </div>
        <div className="table-scroll">
          <table>
            <thead><tr><th>省份</th><th>城市</th><th>活跃终端</th><th>已渗透</th><th>待开发</th><th>渗透率</th><th>单品销售额</th><th>机会判断</th></tr></thead>
            <tbody>
              {geography.map((row) => (
                <tr key={`${row.province}-${row.city}`}>
                  <td>{row.province}</td><td className="cell-strong">{row.city}</td><td>{row.totalTerminals}</td><td>{row.targetTerminals}</td><td>{row.gap}</td>
                  <td><span className="table-progress"><i style={{ width: `${Math.min(100, row.penetration)}%` }} /><b>{formatPercent(row.penetration)}</b></span></td>
                  <td>{formatCompactCurrency(row.targetSales)}</td>
                  <td><span className={`status-pill ${row.penetration >= 70 ? 'good' : row.penetration >= 40 ? 'medium' : 'attention'}`}>{row.penetration >= 70 ? '巩固' : row.penetration >= 40 ? '提升' : '重点开发'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
